import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { getCurrentUser } from '../../utils/auth';
import CreateAppointmentModal from '../Calendar/CreateAppointmentModal';
import { AppointmentService } from '../../services/appointmentService';

const ClientRejectionModal = ({
  isOpen,
  onClose,
  orderData,
  onRejectionComplete,
  customerAppointments = [] // NEW: Pass from WorkOrderProfile
}) => {
  console.log('🔍 ClientRejectionModal rendered with props:', {
    isOpen,
    orderData: !!orderData,
    customerAppointments: customerAppointments.length
  });

  const API_URL = API_CONFIG.API_URL;

  // UPDATED: 4 steps instead of 3
  const [currentStep, setCurrentStep] = useState(1);
  const [rejectionData, setRejectionData] = useState({
    originalTasks: [],
    removedTasks: [],     // NEW: Tasks removed in Step 1
    repairItems: [],
    newTasks: [],
    reason: '',
    notes: '',
    customRepair: { description: '', price: '' },
    // NEW: Appointment management
    appointmentAction: null, // 'create', 'modify', or 'select'
    appointmentData: null,
    selectedAppointmentId: null
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [repairOptions, setRepairOptions] = useState([]);
  const [repairSearchQuery, setRepairSearchQuery] = useState('');
  const [roomsData, setRoomsData] = useState({
    Pomona: { rooms: [], tasks: {}, workers: {} },
    Lakewood: { rooms: [], tasks: {}, workers: {} },
    Israel: { rooms: [], tasks: {}, workers: {} },
    China: { rooms: [], tasks: {}, workers: {} }
  });

  // NEW: Show CreateAppointmentModal
  const [showCreateAppointmentModal, setShowCreateAppointmentModal] = useState(false);

  // Debug logging for orderData
  useEffect(() => {
    if (showCreateAppointmentModal && orderData) {
      console.log('🔍 DEBUG - OrderData when opening appointment modal:', orderData);
      console.log('🔍 DEBUG - OrderDetails:', orderData.orderDetails);
      console.log('🔍 DEBUG - CustomerNo:', orderData.orderDetails?.customerNo);
      console.log('🔍 DEBUG - ClientId:', orderData.orderDetails?.clientId);
    }
  }, [showCreateAppointmentModal, orderData]);

  const REQUIRED_QC_ROOMS = ['22001', '10001', '33001'];

  const validateRequiredQCRoom = () => {
    // Check manually added tasks
    const hasQCInNewTasks = rejectionData.newTasks.some(task =>
      REQUIRED_QC_ROOMS.includes(task.roomNo) &&
      task.assignedWorker &&
      task.assignedWorker !== '' &&
      task.assignedWorker !== 'Select Worker'
    );

    // Check auto-added removed tasks
    const hasQCInRemovedTasks = rejectionData.removedTasks.some(task => {
      const roomNo = (task.roomNo || task.RoomNo)?.toString();
      const worker = task.assignedWorker || task.AssignedWorker;
      return REQUIRED_QC_ROOMS.includes(roomNo) &&
             worker &&
             worker !== '' &&
             worker !== 'Select Worker';
    });

    return hasQCInNewTasks || hasQCInRemovedTasks;
  };

  // Fetch repair options when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchRepairOptions();
      fetchRoomsData();

      // Initialize with current order tasks - FILTER OUT REJECTED TASKS
      const nonRejectedTasks = (orderData?.roomTasks || []).filter(task => {
        const status = (task.status || task.taskStatus || task.TaskStatus || '').toLowerCase();
        return !status.includes('reject');
      });

      setRejectionData(prev => ({
        ...prev,
        originalTasks: nonRejectedTasks,
        removedTasks: []
      }));
    }
  }, [isOpen, orderData]);

  const fetchRepairOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/get-repair-options`);
      if (response.ok) {
        const data = await response.json();
        setRepairOptions(data);
      }
    } catch (error) {
      console.error('Error fetching repair options:', error);
    }
  };

  const fetchRoomsData = async () => {
    const locations = ['Pomona', 'Lakewood', 'Israel', 'China'];
    const newRoomsData = { ...roomsData };

    for (const location of locations) {
      try {
        const response = await fetch(`${API_URL}/api/get-rooms/${location}`);
        if (response.ok) {
          const rooms = await response.json();
          newRoomsData[location].rooms = rooms.map(room => ({
            roomNo: room.RoomNo,
            roomName: `${room.RoomNo} ${room.RoomDescription}`,
            parentRoomNo: room.ParentRoomNo
          }));
        }
      } catch (error) {
        console.error(`Error fetching rooms for ${location}:`, error);
      }
    }

    setRoomsData(newRoomsData);
  };

  // NEW: Remove task from rejection list (it will appear in Step 3 as available to re-add)
  const removeOriginalTask = (taskIndex) => {
    const removedTask = rejectionData.originalTasks[taskIndex];

    setRejectionData(prev => ({
      ...prev,
      originalTasks: prev.originalTasks.filter((_, index) => index !== taskIndex),
      removedTasks: [...prev.removedTasks, removedTask]
    }));
  };

  // NEW: Handle appointment creation complete
  const handleAppointmentCreated = (appointmentData) => {
    console.log('✅ Appointment created:', appointmentData);
    setRejectionData(prev => ({
      ...prev,
      appointmentAction: 'create',
      appointmentData: appointmentData
    }));
    setShowCreateAppointmentModal(false);
  };

  // Repair item functions
  const addRepairItem = (repair) => {
    const newRepair = {
      id: Date.now(),
      type: 'repair',
      description: repair.RepairDescription,
      price: repair.RepairCost,
      quantity: 1
    };

    setRejectionData(prev => ({
      ...prev,
      repairItems: [...prev.repairItems, newRepair]
    }));
  };

  const removeRepairItem = (itemId) => {
    setRejectionData(prev => ({
      ...prev,
      repairItems: prev.repairItems.filter(item => item.id !== itemId)
    }));
  };

  // Filter repair options based on search query
  const filteredRepairOptions = repairOptions.filter(repair => {
    if (!repairSearchQuery.trim()) return true;

    const searchLower = repairSearchQuery.toLowerCase();
    const descriptionMatch = repair.RepairDescription?.toLowerCase().includes(searchLower);
    const priceMatch = repair.RepairCost?.toString().includes(searchLower);

    return descriptionMatch || priceMatch;
  });

  // Task management functions
  const addNewTask = () => {
    const newTask = {
      id: Date.now(),
      roomNo: '',
      roomTask: '',
      assignedWorker: '',
      taskDueDate: '',
      taskNotes: '',
      qcNotes: '', // NEW: User-provided QC instructions
      location: 'Pomona',
      status: 'Not Started'
    };

    setRejectionData(prev => ({
      ...prev,
      newTasks: [...prev.newTasks, newTask]
    }));
  };

  const updateNewTask = (taskId, updates) => {
    setRejectionData(prev => ({
      ...prev,
      newTasks: prev.newTasks.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      )
    }));
  };

  const handleRoomChange = async (taskId, location, roomNo) => {
    console.log('=== ROOM CHANGE DEBUG ===');
    console.log('Changing to location:', location, 'room:', roomNo);

    // Update the task with new location and room
    updateNewTask(taskId, {
      location,
      roomNo,
      roomTask: '', // Clear task when room changes
      assignedWorker: '' // Clear worker when room changes
    });

    // Load the room data if we have both location and room
    if (location && roomNo) {
      console.log('Loading room data for:', location, roomNo);
      await loadRoomDataInBackground(location, roomNo);
    }
  };

  const loadRoomDataInBackground = async (location, roomNo) => {
    if (!location || !roomNo) {
      console.log('Missing location or roomNo:', { location, roomNo });
      return;
    }

    try {
      console.log('=== LOADING ROOM DATA ===');
      console.log('Location:', location, 'Room:', roomNo);

      // Check if we already have the tasks
      const existingTasks = roomsData[location]?.tasks?.[roomNo];
      console.log('Existing tasks:', existingTasks);

      // Only fetch if we don't already have the data
      if (!existingTasks || existingTasks.length === 0) {
        console.log('Fetching tasks for room:', roomNo);
        const tasksResponse = await fetch(`${API_URL}/api/get-room-tasks/${roomNo}`);
        if (tasksResponse.ok) {
          const tasks = await tasksResponse.json();
          console.log('Tasks API response:', tasks);

          setRoomsData(prev => {
            const updated = {
              ...prev,
              [location]: {
                ...prev[location],
                tasks: {
                  ...(prev[location]?.tasks || {}),
                  [roomNo]: tasks
                }
              }
            };
            console.log('Updated roomsData with tasks:', updated);
            return updated;
          });
        }
      }

      // Check if we already have the workers
      const existingWorkers = roomsData[location]?.workers?.[roomNo];
      console.log('Existing workers:', existingWorkers);

      if (!existingWorkers || existingWorkers.length === 0) {
        console.log('Fetching workers for room:', roomNo);
        const workersResponse = await fetch(`${API_URL}/api/get-room-workers/${roomNo}`);
        if (workersResponse.ok) {
          const workers = await workersResponse.json();
          console.log('Workers API response:', workers);

          setRoomsData(prev => {
            const updated = {
              ...prev,
              [location]: {
                ...prev[location],
                workers: {
                  ...(prev[location]?.workers || {}),
                  [roomNo]: workers
                }
              }
            };
            console.log('Updated roomsData with workers:', updated);
            return updated;
          });
        }
      }
    } catch (error) {
      console.error('Error loading room data:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const removeNewTask = (taskId) => {
    setRejectionData(prev => ({
      ...prev,
      newTasks: prev.newTasks.filter(task => task.id !== taskId)
    }));
  };

  // Helper function to determine location from roomNo
  const determineLocation = (roomNo) => {
    const roomStr = roomNo?.toString() || '';
    if (roomStr.startsWith('2')) return 'Lakewood';
    if (roomStr.startsWith('3')) return 'Israel';
    if (roomStr.startsWith('4')) return 'China';
    return 'Pomona'; // Default for 7xxx, 1xxx, etc.
  };

  // Helper function to convert removed task to corrective task format
  const convertRemovedTaskToCorrective = (task) => {
    const roomNo = task.roomNo || task.RoomNo;
    const location = task.location || determineLocation(roomNo);

    return {
      id: `removed-${task.id || task.ID}`, // Unique ID for removed tasks
      roomNo: roomNo,
      roomTask: task.roomTask || task.RoomTask,
      assignedWorker: task.assignedWorker || task.AssignedWorker || '',
      taskDueDate: task.taskDueDate || task.TaskDueDate || '',
      taskNotes: task.taskNotes || task.TaskNotes || '',
      qcNotes: task.qcNotes || '', // User-provided QC instructions
      location: location,
      status: 'Not Started',
      isRemovedTask: true // Flag to indicate this was auto-added from Step 1
    };
  };

const handleCompleteRejection = async () => {
  setIsProcessing(true);

  try {
    console.log('🚀 Starting complete rejection process...');
    console.log('📝 Rejection data:', rejectionData);

    // Validate appointment action
    if (!rejectionData.appointmentAction) {
      alert('Please complete Step 2: Appointment Management');
      setIsProcessing(false);
      return;
    }

    // Let backend handle appointment creation/management
    let finalAppointmentData = rejectionData.appointmentData;
    let finalAppointmentAction = rejectionData.appointmentAction;

    // Only send manually added tasks - backend will handle removedTasks conversion
    const validNewTasks = rejectionData.newTasks.filter(task => {
      const isValid = task.roomNo && task.roomTask && task.roomNo !== '';
      if (!isValid) {
        console.warn('Invalid task found:', task);
      }
      return isValid;
    });

    console.log('✅ Manually added tasks:', validNewTasks.length);
    console.log('✅ Tasks to be auto-converted by backend:', rejectionData.removedTasks.length);

    // Prepare the data for the new unified endpoint
    const rejectionPayload = {
      orderNo: orderData.orderDetails.orderNo,
      originalTasks: rejectionData.originalTasks,
      removedTasks: rejectionData.removedTasks,        // Backend will convert these
      repairItems: rejectionData.repairItems,
      newTasks: validNewTasks,                          // Only manually added tasks
      rejectionReason: rejectionData.reason,
      rejectionNotes: rejectionData.notes,
      enteredByUser: getCurrentUser(),
      // NEW: Appointment data (use finalAppointmentData/Action which may be from silent creation)
      appointmentAction: finalAppointmentAction,
      appointmentData: finalAppointmentData,
      appointmentId: rejectionData.selectedAppointmentId
    };

    console.log('📤 Sending to unified rejection endpoint:', rejectionPayload);

    // Call the endpoint that handles everything
    const response = await fetch(`${API_URL}/api/process-client-rejection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rejectionPayload)
    });

    console.log('📡 Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Rejection processing failed:', errorText);
      throw new Error(`Rejection processing failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ Rejection processing result:', result);

    if (!result.success) {
      throw new Error(result.error || 'Unknown error occurred');
    }

    // Prepare complete rejection data for parent callback
    const completeRejectionData = {
      ...rejectionData,
      success: true,
      results: result.results,
      errors: result.errors
    };

    // Call parent callback to refresh data
    if (onRejectionComplete) {
      console.log('🔄 Calling parent callback to refresh data...');
      await onRejectionComplete(completeRejectionData);
    }

    // Close modal
    onClose();

    // Show detailed success message
    const { originalTasksUpdated, repairItemsAdded, newTasksAdded, totalErrors } = result.results;

    let successMessage = `Client rejection completed successfully!\n\n`;
    successMessage += `• ${originalTasksUpdated} original task(s) marked as rejected\n`;
    successMessage += `• ${repairItemsAdded} repair item(s) added to order\n`;
    successMessage += `• ${newTasksAdded} corrective task(s) added to production schedule`;

    if (totalErrors > 0) {
      successMessage += `\n\nNote: ${totalErrors} error(s) occurred during processing. Check console for details.`;
    }

    alert(successMessage);

  } catch (error) {
    console.error('❌ Error completing rejection:', error);
    console.error('❌ Error stack:', error.stack);
    alert('Error completing rejection: ' + error.message);
  } finally {
    setIsProcessing(false);
  }
};

  const isCompleteDisabled = isProcessing ||
    rejectionData.newTasks.filter(t => t.roomNo && t.roomTask).length === 0 ||
    !validateRequiredQCRoom() ||
    !rejectionData.appointmentAction; // NEW: Appointment must be set

  const getTaskCardStyle = (task) => {
    const baseStyle = {
      background: 'var(--dark-elevated)',
      border: '2px solid var(--dark-border)',
      borderRadius: '12px',
      padding: '1.5rem',
      transition: 'all 0.3s ease'
    };

    // Highlight QC rooms
    if (REQUIRED_QC_ROOMS.includes(task.roomNo)) {
      return {
        ...baseStyle,
        border: '2px solid #fbbf24',
        background: 'rgba(251, 191, 36, 0.1)'
      };
    }

    return baseStyle;
  };

  // const getRoomLabel = (roomNo) => {
  //   if (REQUIRED_QC_ROOMS.includes(roomNo)) {
  //     return (
  //       <span style={{
  //         background: '#fbbf24',
  //         color: '#000',
  //         padding: '0.25rem 0.5rem',
  //         borderRadius: '4px',
  //         fontSize: '0.8rem',
  //         fontWeight: '600',
  //         marginLeft: '0.5rem',
  //         maxWidth: '50%',
  //       }}>
  //         QC REQUIRED
  //       </span>
  //     );
  //   }
  //   return null;
  // };

  // RENDER STEP CONTENT
  const renderStepContent = () => {
    switch (currentStep) {
      // STEP 1: Select Tasks to Reject
      case 1:
        return renderStep1();

      // STEP 2: Appointment Management (NEW)
      case 2:
        return renderStep2();

      // STEP 3: Add Corrective Tasks (with removed tasks available)
      case 3:
        return renderStep3();

      // STEP 4: Add Repair Items (moved from Step 2)
      case 4:
        return renderStep4();

      default:
        return null;
    }
  };

  // STEP 1 RENDER - Tasks to Reject
  const renderStep1 = () => (
    <div>
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: 'var(--dark-elevated)',
        borderRadius: '12px',
        borderLeft: '5px solid var(--purple-accent)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
      }}>
        <h4 style={{
          color: 'var(--text-primary)',
          fontSize: '1.4rem',
          fontWeight: '700',
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <span style={{ color: '#fbbf24' }}>⚠️</span>
          Select Tasks to Reject
        </h4>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '1.1rem',
          lineHeight: '1.6',
          margin: 0
        }}>
          Click the X to remove tasks you want to keep. Removed tasks will be marked as "Task Completed" and transferred to the prep room.
        </p>
      </div>

      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h6 style={{
          color: 'var(--text-primary)',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '1rem'
        }}>
          Current Production Tasks (will be marked as rejected):
        </h6>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {rejectionData.originalTasks.map((task, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '1rem',
              background: 'var(--dark-elevated)',
              borderRadius: '8px',
              border: '1px solid var(--dark-border)'
            }}>
              <span style={{
                background: 'var(--purple-accent)',
                color: 'white',
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: '600',
                marginRight: '1rem'
              }}>
                {task.roomNo || task.RoomNo}
              </span>
              <span style={{
                flex: 1,
                color: 'var(--text-primary)',
                fontSize: '1.1rem'
              }}>
                {task.roomTask || task.RoomTask}
              </span>
              <small style={{
                color: 'var(--text-secondary)',
                fontSize: '1rem',
                marginRight: '1rem'
              }}>
                Assigned: {task.assignedWorker || task.AssignedWorker}
              </small>
              <button
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: 'white',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                }}
                onClick={() => removeOriginalTask(index)}
                title="Remove from rejection list"
              >
                ×
              </button>
            </div>
          ))}
          {rejectionData.originalTasks.length === 0 && (
            <div style={{
              color: 'var(--text-secondary)',
              fontStyle: 'italic',
              fontSize: '1.1rem',
              textAlign: 'center',
              padding: '2rem',
              background: 'var(--dark-elevated)',
              borderRadius: '8px'
            }}>
              No tasks selected for rejection
            </div>
          )}
        </div>
      </div>

      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <label style={{
          color: 'var(--text-primary)',
          fontSize: '1.1rem',
          fontWeight: '600',
          marginBottom: '0.75rem',
          display: 'block'
        }}>
          Rejection Reason <span style={{ color: '#ef4444' }}>*</span>:
        </label>
        <textarea
          style={{
            background: 'var(--dark-elevated)',
            border: `2px solid ${rejectionData.reason.trim() === '' ? '#ef4444' : 'var(--dark-border)'}`,
            borderRadius: '8px',
            padding: '0.875rem 1rem',
            color: 'var(--text-primary)',
            fontSize: '1.1rem',
            width: '100%',
            resize: 'vertical',
            fontFamily: 'inherit',
            outline: 'none'
          }}
          value={rejectionData.reason}
          onChange={(e) => setRejectionData(prev => ({ ...prev, reason: e.target.value }))}
          placeholder="Please describe why the wig is rejected and where it is currently located..."
          rows="4"
          required
        />
        {rejectionData.reason.trim() === '' && (
          <span style={{
            color: '#ef4444',
            fontSize: '0.875rem',
            marginTop: '0.5rem',
            display: 'block'
          }}>
            Rejection reason is required
          </span>
        )}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '2rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid var(--dark-border)'
      }}>
        <button
          style={{
            padding: '0.875rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: '2px solid var(--dark-border)',
            cursor: 'pointer',
            background: 'var(--dark-elevated)',
            color: 'var(--text-secondary)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
          }}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          style={{
            padding: '0.875rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: 'none',
            cursor: (rejectionData.originalTasks.length === 0 || rejectionData.reason.trim() === '') ? 'not-allowed' : 'pointer',
            background: (rejectionData.originalTasks.length === 0 || rejectionData.reason.trim() === '') ? 'var(--dark-border)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            opacity: (rejectionData.originalTasks.length === 0 || rejectionData.reason.trim() === '') ? 0.5 : 1
          }}
          onClick={() => setCurrentStep(2)}
          disabled={rejectionData.originalTasks.length === 0 || rejectionData.reason.trim() === ''}
        >
          Continue to Appointment
          <span>→</span>
        </button>
      </div>
    </div>
  );

  // STEP 2 RENDER - Appointment Management (NEW)
  const renderStep2 = () => (
    <div>
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: 'var(--dark-elevated)',
        borderRadius: '12px',
        borderLeft: '5px solid #3b82f6',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
      }}>
        <h4 style={{
          color: 'var(--text-primary)',
          fontSize: '1.4rem',
          fontWeight: '700',
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <span style={{ color: '#3b82f6' }}>📅</span>
          Due Date Management
        </h4>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '1.1rem',
          lineHeight: '1.6',
          margin: 0
        }}>
          Choose whether to keep or modify the order due date and customer pickup date.
        </p>
      </div>

      {/* Appointment Action Selector */}
      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h6 style={{
          color: 'var(--text-primary)',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '1rem'
        }}>
          Choose Action <span style={{ color: '#ef4444' }}>*</span>:
        </h6>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Keep Current Due Date */}
          <div
            onClick={() => setRejectionData(prev => ({
              ...prev,
              appointmentAction: 'keep',
              appointmentData: null
            }))}
            style={{
              padding: '1.5rem',
              background: rejectionData.appointmentAction === 'keep' ? 'rgba(59, 130, 246, 0.1)' : 'var(--dark-elevated)',
              border: rejectionData.appointmentAction === 'keep' ? '2px solid #3b82f6' : '2px solid var(--dark-border)',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <input
                type="radio"
                checked={rejectionData.appointmentAction === 'keep'}
                readOnly
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  Keep Current Due Date
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Use the existing due date without changes
                </div>
                {rejectionData.appointmentAction === 'keep' && orderData?.orderDetails?.dueDate && (
                  <div style={{ color: '#3b82f6', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    ✓ Current due date: {(() => {
                      // Display UTC time converted to EST (subtract 5 hours)
                      const dateStr = orderData.orderDetails.dueDate;
                      if (!dateStr) return 'N/A';

                      // Parse the raw date components without creating a Date object
                      let dateParts, timeParts;

                      if (dateStr.includes('T')) {
                        // ISO format: 2025-12-23T14:15:00.000Z or 2025-12-23T14:15:00
                        const [datePart, timePart] = dateStr.split('T');
                        dateParts = datePart.split('-');
                        // Remove 'Z' or any timezone info from time part
                        timeParts = timePart.replace('Z', '').split('.')[0].split(':');
                      } else {
                        // Direct format: 2025-12-23 14:15:00
                        const [datePart, timePart] = dateStr.split(' ');
                        dateParts = datePart.split('-');
                        timeParts = timePart ? timePart.split(':') : ['00', '00'];
                      }

                      let year = parseInt(dateParts[0]);
                      let month = parseInt(dateParts[1]);
                      let day = parseInt(dateParts[2]);
                      let hour = parseInt(timeParts[0]);
                      let minute = parseInt(timeParts[1]);

                      // Convert UTC to EST by subtracting 5 hours
                      hour -= 5;

                      // Handle date rollover if hour goes negative
                      if (hour < 0) {
                        hour += 24;
                        day -= 1;

                        // Handle month rollover if day goes to 0
                        if (day === 0) {
                          month -= 1;
                          if (month === 0) {
                            month = 12;
                            year -= 1;
                          }
                          // Get last day of previous month
                          const daysInMonth = new Date(year, month, 0).getDate();
                          day = daysInMonth;
                        }
                      }

                      // Format directly from the parsed values
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const period = hour >= 12 ? 'PM' : 'AM';
                      const displayHour = hour % 12 || 12;
                      const displayMinute = String(minute).padStart(2, '0');

                      return `${monthNames[month - 1]} ${day}, ${year}, ${displayHour}:${displayMinute} ${period} EST`;
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modify Due Date/Time */}
          <div
            onClick={() => setRejectionData(prev => ({ ...prev, appointmentAction: 'modify' }))}
            style={{
              padding: '1.5rem',
              background: rejectionData.appointmentAction === 'modify' ? 'rgba(59, 130, 246, 0.1)' : 'var(--dark-elevated)',
              border: rejectionData.appointmentAction === 'modify' ? '2px solid #3b82f6' : '2px solid var(--dark-border)',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <input
                type="radio"
                checked={rejectionData.appointmentAction === 'modify'}
                readOnly
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  Modify Due Date/Time
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Change the due date/time for this order and customer pickup task
                </div>
                {rejectionData.appointmentAction === 'modify' && (
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ color: 'var(--text-primary)', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>
                      New Date & Time:
                    </label>
                    <input
                      type="datetime-local"
                      style={{
                        background: 'var(--dark-elevated)',
                        border: '2px solid var(--dark-border)',
                        borderRadius: '8px',
                        padding: '0.5rem',
                        color: 'var(--text-primary)',
                        fontSize: '1rem'
                      }}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        setRejectionData(prev => ({
                          ...prev,
                          appointmentData: {
                            EventStart: dateValue,
                            EventEnd: dateValue
                          }
                        }));
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>


        </div>
      </div>

      {/* Navigation Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '2rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid var(--dark-border)'
      }}>
        <button
          style={{
            padding: '0.875rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: '2px solid var(--dark-border)',
            cursor: 'pointer',
            background: 'var(--dark-elevated)',
            color: 'var(--text-secondary)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
          }}
          onClick={() => setCurrentStep(1)}
        >
          ← Back
        </button>
        <button
          style={{
            padding: '0.875rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: 'none',
            cursor: !rejectionData.appointmentAction ? 'not-allowed' : 'pointer',
            background: !rejectionData.appointmentAction ? 'var(--dark-border)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            opacity: !rejectionData.appointmentAction ? 0.5 : 1
          }}
          onClick={() => setCurrentStep(3)}
          disabled={!rejectionData.appointmentAction}
        >
          Continue to Corrective Tasks
          <span>→</span>
        </button>
      </div>
    </div>
  );

  // STEP 3 RENDER - Add Corrective Tasks (showing removed tasks)
  const renderStep3 = () => (
    <div>
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: 'var(--dark-elevated)',
        borderRadius: '12px',
        borderLeft: '5px solid var(--green-accent)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
      }}>
        <h4 style={{
          color: 'var(--text-primary)',
          fontSize: '1.4rem',
          fontWeight: '700',
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <span style={{ color: 'var(--green-accent)' }}>✓</span>
          Add Corrective Room Tasks
        </h4>

        {/* QC Room Requirement Warning */}
        {!validateRequiredQCRoom() && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '2px solid #ef4444',
            borderRadius: '12px',
            padding: '1.5rem',
            marginTop: '1rem',
            marginBottom: '1rem'
          }}>
            <h6 style={{
              color: '#ef4444',
              fontSize: '1.2rem',
              fontWeight: '600',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              ⚠️ Quality Control Required
            </h6>
            <p style={{
              color: '#fca5a5',
              fontSize: '1rem',
              margin: 0
            }}>
              You must add at least one Quality Control room ({REQUIRED_QC_ROOMS.join(', ')}) with an assigned worker before completing the rejection.
            </p>
          </div>
        )}
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '1.1rem',
          lineHeight: '1.6',
          margin: 0
        }}>
          Add corrective work tasks. Prep and Customer Pickup rooms will be auto-added.
        </p>
      </div>

      {/* Show Removed Tasks - Automatically Added */}
      {rejectionData.removedTasks.length > 0 && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '2px solid #10b981',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h6 style={{
            color: '#10b981',
            fontSize: '1.2rem',
            fontWeight: '600',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            ✅ Tasks Kept from Rejection (Marked as Completed)
          </h6>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1rem',
            marginBottom: '1rem'
          }}>
            These tasks will be marked as "Task Completed" and transferred to the prep room automatically.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {rejectionData.removedTasks.map((task, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem',
                  background: 'var(--dark-elevated)',
                  borderRadius: '8px',
                  border: '1px solid var(--dark-border)'
                }}
              >
                <span style={{
                  background: '#10b981',
                  color: 'white',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  marginRight: '1rem'
                }}>
                  {task.roomNo || task.RoomNo}
                </span>
                <span style={{
                  flex: 1,
                  color: 'var(--text-primary)',
                  fontSize: '1.1rem'
                }}>
                  {task.roomTask || task.RoomTask}
                </span>
                <span style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.95rem'
                }}>
                  {task.assignedWorker || task.AssignedWorker || 'No worker assigned'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Corrective Tasks Section */}
      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h6 style={{
            color: 'var(--text-primary)',
            fontSize: '1.2rem',
            fontWeight: '600',
            margin: 0
          }}>
            Corrective Tasks:
          </h6>
          <button
            style={{
              padding: '0.875rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, var(--green-accent), var(--green-light))',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}
            onClick={addNewTask}
          >
            <span>+</span>
            Add Task
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
          {rejectionData.newTasks.map((task) => (
            <div key={task.id} style={{ ...getTaskCardStyle(task), overflow: 'hidden', boxSizing: 'border-box' }}>
              {/* First row: Location, Room, Task, Delete */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto',
                gap: '1rem',
                marginBottom: '1rem',
                width: '100%'
              }}>
                <select
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    minWidth: 0,
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  value={task.location}
                  onChange={(e) => {
                    handleRoomChange(task.id, e.target.value, ''); // Clear room when location changes
                  }}
                >
                  <option value="">Select Location</option>
                  <option value="Pomona">Pomona</option>
                  <option value="Lakewood">Lakewood</option>
                  <option value="Israel">Israel</option>
                  <option value="China">China</option>
                </select>

                <select
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    opacity: !task.location ? 0.5 : 1,
                    cursor: !task.location ? 'not-allowed' : 'pointer',
                    minWidth: 0,
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  value={task.roomNo}
                  onChange={(e) => handleRoomChange(task.id, task.location, e.target.value)}
                  disabled={!task.location}
                >
                  <option value="">Select Room</option>
                  {(roomsData[task.location]?.rooms || []).map(room => (
                    <option key={room.roomNo} value={room.roomNo}>
                      {room.roomName}
                    </option>
                  ))}
                </select>
                {/* {task.roomNo } */}

                <select
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    opacity: !task.roomNo ? 0.5 : 1,
                    cursor: !task.roomNo ? 'not-allowed' : 'pointer',
                    minWidth: 0,
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  value={task.roomTask}
                  onChange={(e) => updateNewTask(task.id, { roomTask: e.target.value })}
                  disabled={!task.roomNo}
                >
                  <option value="">Select Task</option>
                  {(() => {
                    const location = task.location;
                    const roomNo = task.roomNo;
                    const tasks = roomsData[location]?.tasks?.[roomNo] || [];

                    if (tasks.length === 0) {
                      return <option value="" disabled>No tasks available for this room</option>;
                    }

                    return tasks.map((roomTask, taskIndex) => {
                      const taskDescription = roomTask.RoomTask || roomTask.TaskDescription || roomTask.taskDescription || '';
                      return (
                        <option key={taskIndex} value={taskDescription}>
                          {taskDescription}
                        </option>
                      );
                    });
                  })()}
                </select>

                <button
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                  }}
                  onClick={() => removeNewTask(task.id)}
                  title="Remove task"
                >
                  ×
                </button>
              </div>

              {/* Second row: Worker assignment and QC Notes */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 2fr',
                gap: '1rem',
                width: '100%'
              }}>
                <select
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    opacity: !task.roomNo ? 0.5 : 1,
                    cursor: !task.roomNo ? 'not-allowed' : 'pointer',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  value={task.assignedWorker}
                  onChange={(e) => updateNewTask(task.id, { assignedWorker: e.target.value })}
                  disabled={!task.roomNo}
                >
                  <option value="">Select Worker</option>
                  {(() => {
                    const location = task.location;
                    const roomNo = task.roomNo;
                    const workers = roomsData[location]?.workers?.[roomNo] || [];

                    if (workers.length === 0) {
                      return <option value="" disabled>No workers available for this room</option>;
                    }

                    return workers.map((worker, workerIndex) => {
                      const workerName = worker.UserName || worker.userName || '';
                      return (
                        <option key={workerIndex} value={workerName}>
                          {workerName}
                        </option>
                      );
                    });
                  })()}
                </select>

                <input
                  type="text"
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  value={task.qcNotes || ''}
                  onChange={(e) => updateNewTask(task.id, { qcNotes: e.target.value })}
                  placeholder="Task Notes (optional)"
                />
              </div>
            </div>
          ))}

          {rejectionData.newTasks.length === 0 && (
            <div style={{
              color: 'var(--text-secondary)',
              fontStyle: 'italic',
              fontSize: '1.1rem',
              textAlign: 'center',
              padding: '2rem',
              background: 'var(--dark-elevated)',
              borderRadius: '8px'
            }}>
              No corrective tasks added yet. Click "Add Task" to begin.
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '2rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid var(--dark-border)'
      }}>
        <button
          style={{
            padding: '0.875rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: '2px solid var(--dark-border)',
            cursor: 'pointer',
            background: 'var(--dark-elevated)',
            color: 'var(--text-secondary)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
          }}
          onClick={() => setCurrentStep(2)}
        >
          ← Back
        </button>
        <button
          style={{
            padding: '0.875rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: 'none',
            cursor: !validateRequiredQCRoom() ? 'not-allowed' : 'pointer',
            background: !validateRequiredQCRoom() ? 'var(--dark-border)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            opacity: !validateRequiredQCRoom() ? 0.5 : 1
          }}
          onClick={() => setCurrentStep(4)}
          disabled={!validateRequiredQCRoom()}
        >
          Continue to Repairs
          <span>→</span>
        </button>
      </div>
    </div>
  );

  // STEP 4 RENDER - Add Repair Items (moved from old Step 2)
  const renderStep4 = () => (
    <div>
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: 'var(--dark-elevated)',
        borderRadius: '12px',
        borderLeft: '5px solid var(--green-accent)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
      }}>
        <h4 style={{
          color: 'var(--text-primary)',
          fontSize: '1.4rem',
          fontWeight: '700',
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <span style={{ color: 'var(--green-accent)' }}>🔧</span>
          Add Corrective Repairs (Optional)
        </h4>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '1.1rem',
          lineHeight: '1.6',
          margin: 0
        }}>
          Select repairs needed to address the client's concerns, or skip this step if no repairs are needed.
        </p>
      </div>

      {/* Custom Repair Input */}
      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h6 style={{
          color: 'var(--text-primary)',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '1rem'
        }}>
          Add Custom Repair:
        </h6>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: '1rem',
          alignItems: 'end'
        }}>
          <input
            type="text"
            style={{
              background: 'var(--dark-elevated)',
              border: '2px solid var(--dark-border)',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              color: 'var(--text-primary)',
              fontSize: '1.1rem',
              fontFamily: 'inherit'
            }}
            placeholder="Enter custom repair description..."
            value={rejectionData.customRepair?.description || ''}
            onChange={(e) => setRejectionData(prev => ({
              ...prev,
              customRepair: {
                ...prev.customRepair,
                description: e.target.value
              }
            }))}
          />
          <input
            type="number"
            step="0.01"
            style={{
              background: 'var(--dark-elevated)',
              border: '2px solid var(--dark-border)',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              color: 'var(--text-primary)',
              fontSize: '1.1rem',
              width: '120px',
              fontFamily: 'inherit'
            }}
            placeholder="Price"
            value={rejectionData.customRepair?.price || ''}
            onChange={(e) => setRejectionData(prev => ({
              ...prev,
              customRepair: {
                ...prev.customRepair,
                price: e.target.value
              }
            }))}
          />
          <button
            style={{
              padding: '0.875rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              border: 'none',
              cursor: (!rejectionData.customRepair?.description || !rejectionData.customRepair?.price) ? 'not-allowed' : 'pointer',
              background: (!rejectionData.customRepair?.description || !rejectionData.customRepair?.price) ? 'var(--dark-border)' : 'linear-gradient(135deg, var(--green-accent), var(--green-light))',
              color: 'white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              opacity: (!rejectionData.customRepair?.description || !rejectionData.customRepair?.price) ? 0.5 : 1
            }}
            onClick={() => {
              if (rejectionData.customRepair?.description && rejectionData.customRepair?.price) {
                addRepairItem({
                  RepairDescription: rejectionData.customRepair.description,
                  RepairCost: parseFloat(rejectionData.customRepair.price)
                });
                setRejectionData(prev => ({
                  ...prev,
                  customRepair: { description: '', price: '' }
                }));
              }
            }}
            disabled={!rejectionData.customRepair?.description || !rejectionData.customRepair?.price}
          >
            Add
          </button>
        </div>
      </div>

      {/* Pre-defined Repairs */}
      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h6 style={{
          color: 'var(--text-primary)',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '1rem'
        }}>
          Pre-defined Repairs:
        </h6>

        {/* Search Bar */}
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search repairs by description or price..."
            value={repairSearchQuery}
            onChange={(e) => setRepairSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              border: '1px solid var(--dark-border)',
              borderRadius: '8px',
              background: 'var(--dark-elevated)',
              color: 'var(--text-primary)',
              outline: 'none',
              transition: 'border-color 0.3s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--purple-accent)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--dark-border)'}
          />
          {repairSearchQuery && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text-secondary)'
            }}>
              Found {filteredRepairOptions.length} repair{filteredRepairOptions.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1rem',
          maxHeight: '300px',
          overflowY: 'auto',
          padding: '0.5rem'
        }}>
          {filteredRepairOptions.length > 0 ? (
            filteredRepairOptions.map((repair, index) => (
            <div key={index} style={{
              background: 'var(--dark-elevated)',
              border: '1px solid var(--dark-border)',
              borderRadius: '8px',
              padding: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'all 0.3s ease'
            }}>
              <div>
                <div style={{
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  fontSize: '1.1rem',
                  marginBottom: '0.25rem'
                }}>
                  {repair.RepairDescription}
                </div>
                <div style={{
                  color: 'var(--text-secondary)',
                  fontSize: '1rem'
                }}>
                  ${parseFloat(repair.RepairCost).toFixed(2)}
                </div>
              </div>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, var(--green-accent), var(--green-light))',
                  color: 'white',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                }}
                onClick={() => addRepairItem(repair)}
              >
                +
              </button>
            </div>
          ))
          ) : (
            <div style={{
              color: 'var(--text-secondary)',
              textAlign: 'center',
              padding: '2rem',
              gridColumn: '1 / -1'
            }}>
              No repairs found
            </div>
          )}
        </div>
      </div>

      {/* Selected Repairs */}
      {rejectionData.repairItems.length > 0 && (
        <div style={{
          background: 'var(--dark-surface)',
          border: '1px solid var(--dark-border)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h6 style={{
            color: 'var(--text-primary)',
            fontSize: '1.2rem',
            fontWeight: '600',
            marginBottom: '1rem'
          }}>
            Selected Repairs:
          </h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {rejectionData.repairItems.map((item) => (
              <div key={item.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                background: 'var(--dark-elevated)',
                borderRadius: '8px',
                border: '1px solid var(--dark-border)'
              }}>
                <div>
                  <div style={{
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    marginBottom: '0.25rem'
                  }}>
                    {item.description}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>
                    ${parseFloat(item.price).toFixed(2)}
                  </div>
                </div>
                <button
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                  }}
                  onClick={() => removeRepairItem(item.id)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '2rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid var(--dark-border)'
      }}>
        <button
          style={{
            padding: '0.875rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: '2px solid var(--dark-border)',
            cursor: 'pointer',
            background: 'var(--dark-elevated)',
            color: 'var(--text-secondary)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
          }}
          onClick={() => setCurrentStep(3)}
        >
          ← Back
        </button>
        <button
          style={{
            padding: '0.875rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: 'none',
            cursor: isCompleteDisabled ? 'not-allowed' : 'pointer',
            background: isCompleteDisabled ? 'var(--dark-border)' : 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            opacity: isCompleteDisabled ? 0.5 : 1
          }}
          onClick={handleCompleteRejection}
          disabled={isCompleteDisabled}
        >
          {isProcessing ? 'Processing...' : 'Complete Rejection'}
          <span>✓</span>
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem'
      }}>
        <div style={{
          background: 'var(--dark-surface)',
          borderRadius: '16px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          border: '1px solid var(--dark-border)'
        }}>
          {/* Header */}
          <div style={{
            padding: '2rem',
            borderBottom: '1px solid var(--dark-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{
                margin: 0,
                color: 'var(--text-primary)',
                fontSize: '1.8rem',
                fontWeight: '700'
              }}>
                Client Rejection - Order #{orderData?.orderDetails?.orderNo}
              </h3>
              <div style={{
                marginTop: '0.5rem',
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center'
              }}>
                {[1, 2, 3, 4].map(step => (
                  <div key={step} style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    background: currentStep === step ? 'var(--purple-accent)' : 'var(--dark-elevated)',
                    color: currentStep === step ? 'white' : 'var(--text-secondary)',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    Step {step}
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '2rem',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                padding: '0',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div style={{
            padding: '2rem',
            overflowY: 'auto',
            flex: 1
          }}>
            {renderStepContent()}
          </div>
        </div>
      </div>


      {/* CreateAppointmentModal */}
{showCreateAppointmentModal && (
<CreateAppointmentModal
  isOpen={showCreateAppointmentModal}
  onClose={() => setShowCreateAppointmentModal(false)}
  onAppointmentCreated={handleAppointmentCreated}
  prefillData={{
    ClientID: orderData?.orderDetails?.customerNo || orderData?.orderDetails?.clientId || orderData?.orderDetails?.ClientID || '',
    EventDesc: `Order #${orderData?.orderDetails?.orderNo || ''}`,
    Title: 'Customer Pickup' //appointment type
  }}
/>

)}

    </>
  );
};

export default ClientRejectionModal;
